'use client';

import * as React from 'react';
import { useTranslation } from 'react-i18next';

import type { PopoverTriggerProps } from '@radix-ui/react-popover';
import { toZonedTime } from 'date-fns-tz';
import { Button } from '@systemzero-inc/ui/primitives/button';
import { Calendar, type CalendarProps } from '~/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { cn } from '~/lib/utils';

export interface DatePickerProps extends React.HTMLAttributes<HTMLDivElement> {
  value: string;
  onDateChange?: (date?: Date) => void;
  popoverTitle?: React.ReactNode;
  popoverTriggerProps?: PopoverTriggerProps;
  disabled?: boolean;
  disabledDays?: CalendarProps['disabled'];
  defaultCalendarOpen?: boolean;
}

const convertToZonedTime = (date: string) => {
  return toZonedTime(date, 'UTC');
};

export function DatePicker({
  className,
  value,
  onDateChange,
  children,
  popoverTitle,
  disabled,
  disabledDays,
  defaultCalendarOpen = false,
  ...props
}: Readonly<DatePickerProps>) {
  const { t, i18n } = useTranslation();
  const [calendarOpen, setCalendarOpen] = React.useState(defaultCalendarOpen);

  const calendarNavBounds = React.useMemo(() => {
    const y = new Date().getFullYear();
    return {
      startMonth: new Date(y - 25, 0),
      endMonth: new Date(y + 2, 11),
    };
  }, []);

  const resetDate = React.useCallback(() => (value ? convertToZonedTime(value) : undefined), [value]);

  const [date, setDate] = React.useState(resetDate());

  React.useEffect(() => {
    if (value) {
      const convertedDate = convertToZonedTime(value);

      setDate(convertedDate);
    }
  }, [value]);

  const handlePopoverOpenChange = (open: boolean) => {
    if (!open) {
      setDate(resetDate());
    }
    setCalendarOpen(open);
  };

  const handleSelect = (date?: Date) => {
    if (date) setDate(date);
  };

  const handleCancel = () => {
    setDate(resetDate());
    setCalendarOpen(false);
  };

  const handleApply = () => {
    if (onDateChange) {
      onDateChange(date);
    }
    setCalendarOpen(false);
  };

  const calendarFooter = (
    <>
      <Button data-testid="date-picker-cancel-button" variant="outline" onClick={handleCancel}>
        {t('activities.filters.buttons.cancel')}
      </Button>
      <Button data-testid="date-picker-apply-button" onClick={handleApply}>
        {t('activities.filters.buttons.apply')}
      </Button>
    </>
  );

  const defaultAriaLabel = t('common.placeholders.calendar_single');
  const titleAriaLabel = typeof popoverTitle === 'string' ? popoverTitle : defaultAriaLabel;
  const popoverAriaLabel: string = popoverTitle ? titleAriaLabel : defaultAriaLabel;

  return (
    <div data-testid="date-picker-container" className={cn('grid gap-2', className)} {...props}>
      <Popover open={calendarOpen} onOpenChange={handlePopoverOpenChange}>
        <PopoverTrigger disabled={disabled} data-testid="date-picker-trigger">
          {children}
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" aria-label={popoverAriaLabel}>
          {popoverTitle}
          <Calendar
            id={props.id}
            key={props.id}
            mode="single"
            captionLayout="dropdown"
            startMonth={calendarNavBounds.startMonth}
            endMonth={calendarNavBounds.endMonth}
            defaultMonth={date}
            selected={date}
            onSelect={handleSelect}
            timeZone="UTC"
            footer={calendarFooter}
            disabled={disabledDays}
            formatters={{
              formatMonthDropdown: (month) => month.toLocaleString(i18n?.language ?? 'en-US', { month: 'short' }),
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
